/**
 * Client-side error sink — pushes uncaught errors + unhandled promise rejections
 * to the client_errors Supabase table on a 30s buffered flush.
 *
 * I1 (Wren v999999). Complements errorReporter.ts (which fires a per-error webhook
 * out to oakden-hub, or to Sentry) with a self-hosted, family-scoped stream that
 * feeds the weekly-error-digest edge fn. Both can run side-by-side — this sink is
 * cheap and never blocks.
 *
 * Privacy contract:
 *   - Only family_id + timestamp + message + stack + url + user_agent land in the
 *     table. No user_id, no email, no display_name.
 *   - Rate limited to 10 rows / family / rolling hour (client-side counter) so a
 *     React re-render loop can't flood the table.
 *   - No-op until initClientErrorSink() is called AFTER family_id is known
 *     (typically post-sign-in bootstrap in appStore).
 */

import { supabase } from './supabase'

const FLUSH_INTERVAL_MS = 30_000
const MAX_PER_HOUR = 10
const HOUR_MS = 3_600_000
const MAX_STACK_BYTES = 4_000
const MAX_MESSAGE_BYTES = 500

interface QueuedError {
  ts: string
  message: string
  stack: string | null
  url: string
  user_agent: string
}

let installed = false
let familyId: string | null = null
let queue: QueuedError[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null
let sentInLastHour: number[] = []

function truncate(s: string | null | undefined, maxBytes: number): string | null {
  if (s == null) return null
  if (s.length <= maxBytes) return s
  return s.slice(0, maxBytes)
}

function withinRateLimit(): boolean {
  const now = Date.now()
  sentInLastHour = sentInLastHour.filter((t) => now - t < HOUR_MS)
  if (sentInLastHour.length >= MAX_PER_HOUR) return false
  sentInLastHour.push(now)
  return true
}

function enqueue(message: string, stack: string | null): void {
  if (!familyId) return
  if (!withinRateLimit()) return
  queue.push({
    ts: new Date().toISOString(),
    message: truncate(message, MAX_MESSAGE_BYTES) ?? '',
    stack: truncate(stack, MAX_STACK_BYTES),
    url: typeof window !== 'undefined' ? window.location.href : '',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  })
}

async function flush(): Promise<void> {
  if (queue.length === 0 || !familyId) return
  const batch = queue
  queue = []
  const rows = batch.map((e) => ({ ...e, family_id: familyId }))
  try {
    await supabase.from('client_errors').insert(rows)
  } catch {
    // The sink itself must never throw. Drop the batch on failure — the
    // weekly digest is best-effort observability, not a guaranteed audit trail.
  }
}

/**
 * Install window.onerror + unhandledrejection listeners. Idempotent.
 * Call once at app boot; call setClientErrorSinkFamily(id) after auth
 * bootstrap so the sink knows which family to attribute rows to.
 */
export function initClientErrorSink(): void {
  if (installed) return
  if (typeof window === 'undefined') return
  installed = true

  window.addEventListener('error', (evt) => {
    const msg = evt.message ?? 'window.onerror'
    const stack = evt.error && evt.error.stack ? String(evt.error.stack) : null
    enqueue(msg, stack)
  })

  window.addEventListener('unhandledrejection', (evt) => {
    const reason = evt.reason
    const msg =
      typeof reason === 'string'
        ? reason
        : reason && typeof reason === 'object' && 'message' in reason
          ? String((reason as { message: unknown }).message)
          : String(reason)
    const stack =
      reason && typeof reason === 'object' && 'stack' in reason
        ? String((reason as { stack: unknown }).stack)
        : null
    enqueue(`unhandled promise rejection: ${msg}`, stack)
  })

  flushTimer = setInterval(() => {
    void flush()
  }, FLUSH_INTERVAL_MS)

  // Best-effort final flush on unload — sendBeacon would be safer but the
  // supabase-js insert path is Promise-based; skip on unload to avoid blocking.
  window.addEventListener('pagehide', () => {
    void flush()
  })
}

/**
 * Bind the sink to a family_id. Safe to call multiple times as the active
 * family changes (sign-out / switch); the queue is family-scoped only at
 * flush time, so a rebind before flush attributes any pending rows to
 * the LATEST family — acceptable given the sink runs in a single-family
 * session in practice.
 */
export function setClientErrorSinkFamily(id: string | null): void {
  familyId = id
  if (id == null) {
    // Clear pending queue on sign-out so we don't cross-attribute after a
    // subsequent sign-in.
    queue = []
  }
}

/** Test hook — invoke from a sensor flow to verify insert works. */
export function reportClientErrorForTest(message: string): void {
  enqueue(message, null)
  void flush()
}

/** Test hook — force flush now (useful for sensor verification). */
export async function flushClientErrorSinkForTest(): Promise<void> {
  await flush()
}

/** Stop timers — used in tests + hot reload. */
export function teardownClientErrorSink(): void {
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
  queue = []
  sentInLastHour = []
  installed = false
  familyId = null
}
